import React, { useState, useEffect } from 'react';
import { Table, Button, Tooltip, Popover, message } from 'antd';
import { PlusOutlined, CopyOutlined, LinkOutlined } from '@ant-design/icons';
import Axios from 'axios';
import dayjs from 'dayjs';
// テーブル強化 PJ Ph.3: 列リサイズ + ソート + 省略
import {
  PageContainer,
  StatusBadge,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
  dateSorter,
  numberSorter,
} from '../../common';

// トライアルキャンペーン一覧ページ
function TrialCampaignListPage(props) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await Axios.get('/api/trialCampaign/');
      if (res.data.success) {
        setCampaigns(res.data.campaigns);
      } else {
        message.error('キャンペーン情報の取得に失敗しました。');
      }
    } catch (error) {
      message.error('キャンペーン情報の取得中にエラーが発生しました。');
    }
    setLoading(false);
  };

  const handleEdit = (record) => {
    props.history.push(`/maketrialcampaign?id=${record._id}`);
  };

  const handleCreate = () => {
    props.history.push('/maketrialcampaign');
  };

  // トライアル登録用URLをコピー（メールアドレスは {email} プレースホルダのまま）
  // 形式: {フロント origin}/trial_account?email={email}&key={キャンペーンキー}
  // /trial_account はフロントエンドの SPA ルートのため、バックエンドではなく
  // window.location.origin（dev=3000、本番=配信ドメイン）を使う。
  const handleCopyUrl = (record) => {
    const url = `${window.location.origin}/trial_account?email={email}&key=${record.campaign_key}`;
    navigator.clipboard.writeText(url).then(() => {
      message.success(
        '登録用URLをコピーしました。{email} を対象者のメールアドレスに置き換えてください。'
      );
    });
  };

  // テーブル強化 PJ Ph.3: <Column> 子要素 → columns 配列。テキスト列はソート + 省略、
  // 操作列は既存ボタンを維持。列幅ドラッグ・初期フィット・横スクロールは hook が配線。
  const rawColumns = [
    ellipsisColumn({
      title: 'キャンペーン名',
      dataIndex: 'campaign_name',
      key: 'campaign_name',
      width: 220,
      sorter: textSorter('campaign_name'),
    }),
    {
      title: 'キャンペーンキー',
      dataIndex: 'campaign_key',
      key: 'campaign_key',
      width: 200,
      sorter: textSorter('campaign_key'),
      render: (text) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <code>{text}</code>
          <Tooltip title="コピー">
            <CopyOutlined
              style={{ color: 'var(--grey-45)', cursor: 'pointer' }}
              onClick={() => {
                navigator.clipboard.writeText(text).then(() => {
                  message.success('キャンペーンキーをコピーしました。');
                });
              }}
            />
          </Tooltip>
        </span>
      ),
    },
    {
      title: '期間',
      key: 'period',
      width: 200,
      sorter: dateSorter('start_date'),
      render: (_, record) => (
        <span>
          {dayjs(record.start_date).format('YYYY/MM/DD')}
          {' 〜 '}
          {dayjs(record.end_date).format('YYYY/MM/DD')}
        </span>
      ),
    },
    {
      title: 'サーベイ数',
      key: 'surveyCount',
      width: 110,
      align: 'center',
      sorter: (a, b) =>
        (a.survey_seasons || []).length - (b.survey_seasons || []).length,
      render: (_, record) => {
        const seasons = record.survey_seasons || [];
        if (seasons.length === 0) return 0;
        const content = (
          <div>
            {seasons.map((s, i) => (
              <div key={i} style={{ padding: '2px 0' }}>
                {s.survey_name || s.survey_code}
              </div>
            ))}
          </div>
        );
        return (
          <Popover content={content} title="割当サーベイ">
            <span style={{ cursor: 'pointer', color: 'var(--blue-active)', textDecoration: 'underline' }}>
              {seasons.length}
            </span>
          </Popover>
        );
      },
    },
    {
      title: 'トークン有効期限',
      dataIndex: 'token_expire_days',
      key: 'token_expire_days',
      width: 150,
      align: 'center',
      sorter: numberSorter('token_expire_days'),
      render: (days) => `${days}日`,
    },
    {
      title: 'ステータス',
      dataIndex: 'active',
      key: 'active',
      width: 110,
      align: 'center',
      sorter: (a, b) => (a.active ? 1 : 0) - (b.active ? 1 : 0),
      render: (active) =>
        active ? (
          <StatusBadge tone="success">有効</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">無効</StatusBadge>
        ),
    },
    {
      title: '',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Tooltip title="トライアル登録用URLをコピー（{email} は対象者のメールに置換）">
            <Button
              size="small"
              icon={<LinkOutlined />}
              onClick={() => handleCopyUrl(record)}
            >
              URLコピー
            </Button>
          </Tooltip>
          <Button size="small" onClick={() => handleEdit(record)}>
            編集
          </Button>
        </span>
      ),
    },
  ];

  const { columns, components, scroll, wrapRef } = useResizableColumns(rawColumns);

  return (
    <PageContainer fluid>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>トライアルキャンペーン管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新規作成
        </Button>
      </div>

      <div ref={wrapRef}>
        <Table
          columns={columns}
          components={components}
          scroll={scroll}
          dataSource={campaigns}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="middle"
        />
      </div>
    </PageContainer>
  );
}

export default TrialCampaignListPage;
